import { useEffect, useMemo, useState } from 'react';
import styles from './styles.module.scss';
import { useProgressBar } from '@hooks/helpers/useProgressBar';
import { useForm } from '@hooks/helpers/useForm';
import { FormProvider, useWatch } from 'react-hook-form';
import { apiPayBillService, apiScheduleService } from '@api';
import { useLocales } from '@hooks/helpers/useLocales';
import { defaultPaymentData } from '@const/default_form_data';
import { useApiQuery } from '@hooks/api/useApiQuery';
import { motion } from 'framer-motion';
import { pageAnimation } from '@const/animation';
import { useApiMutation } from '@hooks/api/useApiMutation';
import { useUserContext } from '@hooks/context/useUserContext';
import { payBillsPayBillsProgressSteps } from '@const/progress_bar_steps';
import { ENewNotificationType, ESchedulePaymentType, EFundsOperation } from '@enum';
import { EScheduleFrequency } from '@xeppt/xeppt-sdk/types/schedule';
import { ETransactionAction, ETransactionMethod } from '@xeppt/xeppt-sdk/types';
import Recipient from '@sections/send_and_request/steps/recipient';
import Amount from '@sections/send_and_request/steps/amount';
import Schedule from '@sections/send_and_request/steps/schedule';
import Summary from '@sections/send_and_request/steps/summary';
import Success from '@sections/send_and_request/steps/success';
import Button from '@components/common/button';
import { routes } from '@const/routes';
import Typography from '@components/common/typography';
import ProgressBar from '@components/progress_bar';
import { useQueryParams } from '@hooks/helpers/useQueryParams';
import { useNavigationContext } from '@hooks/context/useNavigationContext';
import { isSameDayAsToday, setToCurrentHour } from '@utils/date';
import { useNavigate } from 'react-router-dom';

const PayBills = () => {
    const navigate = useNavigate();
    const { requestSuccessLocale, requestErrorLocale, submitLocale } = useLocales();
    const form = useForm({
        defaultValues: defaultPaymentData
    });
    const [queryPayee, schedule_id] = useQueryParams(['payee', 'id']);
    const { refetchAccount } = useUserContext();
    const isSchedule = useWatch({ name: 'isSchedule', control: form.control });
    const [refetchFeeTrigger, setRefetchFeeTrigger] = useState(0);
    const { handleBack } = useNavigationContext();

    const steps = useMemo(() => {
        return payBillsPayBillsProgressSteps.filter((item) => {
            if (!isSchedule) {
                return item?.id !== '3';
            }
            return true;
        });
    }, [payBillsPayBillsProgressSteps, isSchedule]);
    const { availableStep, prevStep, nextStep, currentStep, changeStep } = useProgressBar(steps, [
        form.watch()
    ]);

    const { data: listPayees } = useApiQuery({
        method: () => apiPayBillService.listPayees(1000, 1),
        notificationType: ENewNotificationType.CARD
    });

    const { handleRequest: createOneTimePayment, isLoading: isPaymentLoading } = useApiMutation({
        method: () => {
            const values = form.getValues();
            const { amount, account } = values;
            return apiPayBillService.createPayment(account, Number(amount));
        },
        onSuccess: () => {
            requestSuccessLocale('create_payment');
            nextStep();
            refetchAccount();
        },
        onError: requestErrorLocale
    });
    const { handleRequest: createSchedulePayment, isLoading: isScheduleLoading } = useApiMutation({
        method: () => {
            const values = form.getValues();
            const {
                paymentFrequency,
                amount,
                number_of_payments,
                next_payment_at,
                isInfinitePayment,
                account
            } = values;
            return apiScheduleService.createSchedule({
                amount: Number(amount || 0),
                next_payment_at: setToCurrentHour(next_payment_at),
                frequency: paymentFrequency,
                number_of_payments: isInfinitePayment ? -1 : Number(number_of_payments),
                reference_id: account,
                method: ETransactionMethod.PAY_BILL,
                action: ETransactionAction.PAYMENT
            });
        },
        onSuccess: () => {
            requestSuccessLocale('create_schedule_payment');
            nextStep();
            refetchAccount();
        },
        onError: requestErrorLocale
    });
    const { handleRequest: updateSchedulePayment, isLoading: isUpdateScheduleLoading } =
        useApiMutation({
            method: () => {
                const values = form.getValues();
                const {
                    paymentFrequency,
                    amount,
                    number_of_payments,
                    next_payment_at,
                    isInfinitePayment
                } = values;
                return apiScheduleService.updateSchedule(schedule_id as string, {
                    amount: Number(amount || 0),
                    next_payment_at: setToCurrentHour(next_payment_at),
                    frequency: paymentFrequency,
                    number_of_payments: isInfinitePayment ? -1 : Number(number_of_payments)
                });
            },
            onSuccess: () => {
                requestSuccessLocale('update_schedule_payment');
                nextStep();
                refetchAccount();
            },
            onError: requestErrorLocale
        });

    useApiQuery({
        method: () => apiScheduleService.getSchedule(schedule_id as string),
        condition: !!schedule_id,
        onSuccess: (data) => {
            form.reset({
                paymentType: ESchedulePaymentType.PAYMENTS_DATE,
                amount: data.amount.toFixed(2),
                account: data.reference_id,
                paymentFrequency: data.frequency,
                next_payment_at: data.next_payment_at,
                number_of_payments: data.remaining_payments,
                isInfinitePayment: data.remaining_payments === -1,
                isSchedule: true
            });
            setRefetchFeeTrigger((val) => val + 1);
        },
        deps: [schedule_id]
    });

    useEffect(() => {
        if (schedule_id) {
            changeStep('2');
        } else {
            changeStep('1');
        }
    }, [schedule_id]);

    useEffect(() => {
        if (queryPayee) {
            form.setValue('account', queryPayee);
        }
    }, [queryPayee]);

    const onSubmit = () => {
        const values = form.getValues();
        const { paymentFrequency, next_payment_at } = values;
        if (schedule_id) {
            updateSchedulePayment(undefined);
        } else if (
            paymentFrequency === EScheduleFrequency.ONCE &&
            isSameDayAsToday(next_payment_at)
        ) {
            createOneTimePayment(undefined);
        } else {
            createSchedulePayment(undefined);
        }
    };

    const handleNextStep = (onSuccess: () => void) => {
        form.trigger().then((isValid) => {
            if (isValid) {
                onSuccess();
            }
        });
    };

    const renderContent = () => {
        switch (currentStep?.id) {
            case '1':
                return (
                    <Recipient
                        payeesList={listPayees?.data || []}
                        onSubmit={() => handleNextStep(nextStep)}
                        method={EFundsOperation.PAY_BILL_CREDIT_PAYMENT}
                    />
                );
            case '2':
                return (
                    <Amount
                        isEdit={!!schedule_id}
                        onPrev={prevStep}
                        method={EFundsOperation.PAY_BILL_CREDIT_PAYMENT}
                        refetchFeeTrigger={refetchFeeTrigger}
                        onSubmit={() =>
                            handleNextStep(() => (isSchedule ? nextStep() : changeStep('4')))
                        }
                    />
                );
            case '3':
                return <Schedule onPrev={prevStep} onSubmit={() => handleNextStep(nextStep)} />;
            case '4':
                return (
                    <Summary
                        isEdit={!!schedule_id}
                        onPrev={prevStep}
                        onSubmit={() =>
                            handleNextStep(() => {
                                onSubmit();
                            })
                        }
                        payeesList={listPayees?.data || []}
                        isLoading={isScheduleLoading || isPaymentLoading || isUpdateScheduleLoading}
                        method={EFundsOperation.PAY_BILL_CREDIT_PAYMENT}
                    />
                );
            case '5':
                return (
                    <Success
                        onReset={() => {
                            form.reset(defaultPaymentData);
                            navigate(routes.pay_bills_pay);
                            changeStep('1');
                        }}
                        isSchedule={isSchedule}
                        method={EFundsOperation.PAY_BILL_CREDIT_PAYMENT}
                    />
                );
        }
    };

    return (
        <motion.div {...pageAnimation} className={styles.main_wrapper}>
            <div className={styles.header}>
                <Button
                    className={styles.back}
                    leftIcon="arrow_left"
                    variant="text-gray"
                    onClick={() => {
                        form.reset(defaultPaymentData);
                        changeStep('1');
                        handleBack(routes.send_and_request);
                    }}>
                    {submitLocale('back')}
                </Button>
                <Typography variant="h4">Pay a bill</Typography>
            </div>
            <div className={styles.content_wrapper}>
                <ProgressBar
                    disabled={!!schedule_id}
                    steps={steps}
                    availableStep={availableStep}
                    currentStep={currentStep}
                    className={styles.progress}
                    changeStep={changeStep}
                />
                <div className={styles.body_wrapper}>
                    <FormProvider {...form}>{renderContent()}</FormProvider>
                </div>
            </div>
        </motion.div>
    );
};

export default PayBills;
